<template>
  <section class="product-table" aria-label="Adoption by product">
    <header class="product-table__header">
      <h3 class="product-table__title">Adoption by product</h3>
      <p class="product-table__subtitle">
        Each product answers three questions: who uses it, what they do, and what result they get.
        Status is per product. Only products turned on are shown.
      </p>
    </header>

    <div class="product-table__scroll">
      <table class="product-table__table">
        <thead>
          <tr>
            <th scope="col" class="product-table__th">Product</th>
            <th scope="col" class="product-table__th">Who uses it</th>
            <th scope="col" class="product-table__th">Key actions · last 30 days</th>
            <th scope="col" class="product-table__th">Result</th>
            <th scope="col" class="product-table__th">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="products.length === 0">
            <td colspan="5" class="product-table__empty">No products to show.</td>
          </tr>
          <tr
            v-for="product in products"
            v-else
            :key="product.key || product.name"
            class="product-table__row"
          >
            <td class="product-table__td">
              <span class="product-table__name">{{ product.name }}</span>
              <span v-if="product.always_on" class="product-table__always-on">Always on</span>
            </td>
            <td class="product-table__td product-table__muted">{{ product.who_uses_it }}</td>
            <td class="product-table__td product-table__muted">{{ product.key_actions }}</td>
            <td class="product-table__td">
              <div v-if="resultIsProgress(product.result)" class="product-table__result-progress">
                <span class="product-table__result-label">{{ product.result.label }}</span>
                <div
                  class="product-table__progress"
                  role="progressbar"
                  :aria-valuenow="progressValue(product.result.value)"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  :aria-label="product.result.label"
                >
                  <div
                    class="product-table__progress-fill"
                    :style="{ width: `${progressValue(product.result.value)}%` }"
                  ></div>
                </div>
              </div>
              <div v-else class="product-table__result-text">
                <span class="product-table__result-value">{{ resultText(product.result) }}</span>
                <span
                  v-if="product.result.trend != null && product.result.trend_direction"
                  class="product-table__result-trend"
                  :class="`product-table__result-trend--${product.result.trend_direction}`"
                >
                  {{ trendText(product.result) }}
                </span>
              </div>
            </td>
            <td class="product-table__td">
              <span
                class="product-table__badge"
                :class="`product-table__badge--${bandClass(product.status)}`"
              >
                {{ bandLabel(product.status) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { defineProps } from "vue";

defineProps({
  products: {
    type: Array,
    default: () => [],
  },
});

const BAND_LABELS = { sticky: "Sticky", watch: "Watch", stalling: "Stalling" };

function bandLabel(status) {
  return BAND_LABELS[status] || BAND_LABELS.stalling;
}

function bandClass(status) {
  const raw = String(status || "").toLowerCase();
  if (raw === "sticky") return "sticky";
  if (raw === "stalling") return "stalling";
  return "watch";
}

function resultIsProgress(result) {
  return result && result.type === "progress";
}

function progressValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function resultText(result) {
  return result?.label ?? result?.value ?? "—";
}

function trendText(result) {
  const direction = result?.trend_direction;
  const raw = result?.trend;
  if (direction === "up") return `▲ ${raw}`;
  if (direction === "down") return `▼ ${raw}`;
  return raw == null ? "" : String(raw);
}
</script>

<style scoped>
.product-table {
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  overflow: hidden;
}

.product-table__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px;
}

.product-table__title {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.product-table__subtitle {
  margin: 0;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.product-table__scroll {
  overflow-x: auto;
  border-top: 1px solid #eaecf0;
}

.product-table__table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
}

.product-table__th {
  padding: 12px 16px;
  text-align: left;
  color: #475467;
  background: #f9fafb;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  border-bottom: 1px solid #eaecf0;
}

.product-table__td {
  padding: 16px;
  text-align: left;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  border-bottom: 1px solid #eaecf0;
  vertical-align: top;
}

.product-table__row:last-child .product-table__td {
  border-bottom: none;
}

.product-table__muted {
  color: #475467;
  font-weight: 400;
}

.product-table__empty {
  padding: 24px 16px;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
}

.product-table__name {
  display: block;
  color: #101828;
  font-weight: 600;
}

.product-table__always-on {
  display: inline-block;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.product-table__result-progress,
.product-table__result-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.product-table__result-label,
.product-table__result-value {
  color: #101828;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.product-table__progress {
  height: 8px;
  width: 140px;
  border-radius: 8px;
  background: #eaecf0;
  overflow: hidden;
}

.product-table__progress-fill {
  height: 100%;
  border-radius: 8px;
  background: #7f56d9;
}

.product-table__result-trend {
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
}

.product-table__result-trend--up {
  color: #067647;
}

.product-table__result-trend--down {
  color: #b42318;
}

.product-table__result-trend--flat {
  color: #667085;
}

.product-table__badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.product-table__badge--sticky {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}

.product-table__badge--watch {
  color: #b54708;
  background: #fffaeb;
  border: 1px solid #fedf89;
}

.product-table__badge--stalling {
  color: #b42318;
  background: #fee4e2;
  border: 1px solid #fecdca;
}
</style>
